import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { 
  Watch, Activity, Heart, Moon, Flame, Footprints, 
  RefreshCw, Link2, Unlink, Smartphone, Circle, Zap,
  CheckCircle2, AlertCircle, Loader2, ExternalLink, TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';

// Device icon mapping
const DEVICE_ICONS = {
  APPLE: Watch,
  FITBIT: Activity,
  GARMIN: Watch,
  GOOGLE: Smartphone,
  SAMSUNG: Smartphone,
  OURA: Circle,
  WHOOP: Activity,
  POLAR: Heart,
  SUUNTO: Watch,
  WITHINGS: Activity,
  XIAOMI: Watch,
  HUAWEI: Watch,
  COROS: Watch,
  AMAZFIT: Watch,
  EIGHT: Moon
};

const DEVICE_COLORS = {
  APPLE: 'from-gray-700 to-gray-900',
  FITBIT: 'from-teal-500 to-cyan-600',
  GARMIN: 'from-blue-600 to-indigo-700',
  GOOGLE: 'from-green-500 to-emerald-600',
  SAMSUNG: 'from-blue-500 to-blue-700',
  OURA: 'from-purple-500 to-violet-600',
  WHOOP: 'from-yellow-500 to-orange-600',
  POLAR: 'from-red-500 to-rose-600',
  SUUNTO: 'from-slate-600 to-slate-800',
  WITHINGS: 'from-emerald-500 to-teal-600',
  XIAOMI: 'from-orange-500 to-amber-600',
  HUAWEI: 'from-red-600 to-red-800',
  COROS: 'from-cyan-500 to-blue-600',
  AMAZFIT: 'from-green-600 to-emerald-700',
  EIGHT: 'from-indigo-500 to-purple-600'
};

const TerraWearables = ({ user, setUser }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [supportedDevices, setSupportedDevices] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [healthSummary, setHealthSummary] = useState(null);
  const [activityData, setActivityData] = useState([]);
  const [sleepData, setSleepData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    // Check if returning from Terra auth
    const status = searchParams.get('status');
    if (status === 'success') {
      toast.success('Wearable connected successfully!');
      navigate('/wearables', { replace: true });
    } else if (status === 'failure') {
      toast.error('Failed to connect wearable. Please try again.');
      navigate('/wearables', { replace: true });
    }
    
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [devicesRes, statusRes, summaryRes] = await Promise.all([
        axios.get(`${API}/terra/supported-devices`),
        axios.get(`${API}/terra/connection-status`),
        axios.get(`${API}/terra/health-data/summary`).catch(() => ({ data: null }))
      ]);
      
      setSupportedDevices(devicesRes.data.devices || []);
      setConnectionStatus(statusRes.data);
      setHealthSummary(summaryRes.data);
      
      // Load historical data if connected
      if (statusRes.data?.is_connected) {
        await loadHealthData();
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load wearables data');
    } finally {
      setLoading(false);
    }
  };

  const loadHealthData = async () => {
    try {
      const [activityRes, sleepRes] = await Promise.all([
        axios.get(`${API}/terra/health-data/activity?days=7`),
        axios.get(`${API}/terra/health-data/sleep?days=7`)
      ]);
      
      setActivityData(activityRes.data?.data || []);
      setSleepData(sleepRes.data?.data || []);
    } catch (error) {
      console.error('Error loading health data:', error);
    }
  };

  const connectDevice = async () => {
    setConnecting(true);
    try {
      const response = await axios.post(`${API}/terra/connect`, {});
      
      if (response.data?.widget_url) {
        // Open Terra widget in new tab
        window.open(response.data.widget_url, '_blank');
        toast.info('Complete the connection in the new tab, then return here.');
        
        // Start polling for connection status
        startConnectionPolling();
      }
    } catch (error) {
      console.error('Error connecting device:', error);
      toast.error(error.response?.data?.detail || 'Failed to initiate connection');
    } finally {
      setConnecting(false);
    }
  };

  const startConnectionPolling = () => {
    const pollInterval = setInterval(async () => {
      try {
        const response = await axios.get(`${API}/terra/connection-status`);
        if (response.data?.is_connected) {
          clearInterval(pollInterval);
          setConnectionStatus(response.data);
          toast.success(`${response.data.provider} connected successfully!`);
          loadData();
        }
      } catch (error) {
        console.error('Polling error:', error);
      }
    }, 3000);
    
    // Stop polling after 5 minutes
    setTimeout(() => clearInterval(pollInterval), 5 * 60 * 1000);
  };

  const disconnectDevice = async () => {
    try {
      await axios.delete(`${API}/terra/disconnect`);
      setConnectionStatus({ is_connected: false, status: 'disconnected' });
      setHealthSummary(null);
      setActivityData([]);
      setSleepData([]);
      toast.success('Wearable disconnected');
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect device');
    }
  };

  const syncData = async () => {
    setSyncing(true);
    try {
      await loadHealthData();
      const summaryRes = await axios.get(`${API}/terra/health-data/summary`);
      setHealthSummary(summaryRes.data);
      toast.success('Data synced successfully');
    } catch (error) {
      console.error('Sync error:', error);
      toast.error('Failed to sync data');
    } finally {
      setSyncing(false);
    }
  };

  const getDeviceIcon = (deviceId) => {
    const Icon = DEVICE_ICONS[deviceId] || Watch;
    return Icon;
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return num.toLocaleString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation user={user} setUser={setUser} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2">Smartwatch Integration</h1>
          <p className="text-gray-400">Connect your wearable device to sync health data automatically</p>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="dashboard" className="data-[state=active]:bg-white/20">Dashboard</TabsTrigger>
            <TabsTrigger value="devices" className="data-[state=active]:bg-white/20">Connect Device</TabsTrigger>
            <TabsTrigger value="activity" className="data-[state=active]:bg-white/20">Activity</TabsTrigger>
            <TabsTrigger value="sleep" className="data-[state=active]:bg-white/20">Sleep</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6">
            {/* Connection Status Card */}
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle className="text-xl">Connection Status</CardTitle>
                  <CardDescription className="text-gray-400">
                    {connectionStatus?.is_connected 
                      ? `Connected to ${connectionStatus.provider}` 
                      : 'No device connected'}
                  </CardDescription>
                </div>
                {connectionStatus?.is_connected ? (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                    <CheckCircle2 className="w-4 h-4 mr-1" />
                    Connected
                  </Badge>
                ) : (
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                    <AlertCircle className="w-4 h-4 mr-1" />
                    Not Connected
                  </Badge>
                )}
              </CardHeader>
              <CardContent>
                {connectionStatus?.is_connected ? (
                  <div className="flex items-center gap-4">
                    <Button
                      onClick={syncData}
                      disabled={syncing}
                      className="bg-teal-600 hover:bg-teal-700"
                    >
                      {syncing ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="w-4 h-4 mr-2" />
                      )}
                      Sync Data
                    </Button>
                    <Button
                      variant="outline"
                      onClick={disconnectDevice}
                      className="border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect
                    </Button>
                    {connectionStatus?.last_sync && (
                      <span className="text-sm text-gray-400">
                        Last sync: {new Date(connectionStatus.last_sync).toLocaleString()}
                      </span>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => setActiveTab('devices')}
                    className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Connect Your Device
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* Health Summary Cards */}
            {healthSummary?.is_connected && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Steps */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-blue-500/20">
                        <Footprints className="w-5 h-5 text-blue-400" />
                      </div>
                      <span className="text-sm text-gray-400">Steps</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(healthSummary.today?.steps)}</p>
                    <Progress 
                      value={Math.min((healthSummary.today?.steps || 0) / 100, 100)} 
                      className="mt-2 h-1" 
                    />
                    <p className="text-xs text-gray-500 mt-1">Goal: 10,000</p>
                  </CardContent>
                </Card>

                {/* Calories */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-orange-500/20">
                        <Flame className="w-5 h-5 text-orange-400" />
                      </div>
                      <span className="text-sm text-gray-400">Calories</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(healthSummary.today?.calories)}</p>
                    <p className="text-xs text-gray-500 mt-1">kcal burned</p>
                  </CardContent>
                </Card>

                {/* Heart Rate */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-red-500/20">
                        <Heart className="w-5 h-5 text-red-400" />
                      </div>
                      <span className="text-sm text-gray-400">Heart Rate</span>
                    </div>
                    <p className="text-2xl font-bold">{formatNumber(healthSummary.today?.heart_rate_avg)}</p>
                    <p className="text-xs text-gray-500 mt-1">avg BPM</p>
                  </CardContent>
                </Card>

                {/* Sleep */}
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardContent className="pt-6">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="p-2 rounded-lg bg-purple-500/20">
                        <Moon className="w-5 h-5 text-purple-400" />
                      </div>
                      <span className="text-sm text-gray-400">Sleep</span>
                    </div>
                    <p className="text-2xl font-bold">{healthSummary.last_sleep?.duration_hours || '—'}h</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {healthSummary.last_sleep?.efficiency 
                        ? `${Math.round(healthSummary.last_sleep.efficiency * 100)}% efficiency`
                        : 'Last night'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            )}

            {/* No Data State */}
            {!healthSummary?.is_connected && (
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="py-12 text-center">
                  <Watch className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Wearable Connected</h3>
                  <p className="text-gray-400 mb-6 max-w-md mx-auto">
                    Connect your smartwatch or fitness tracker to automatically sync your health data.
                    We support Apple Watch, Fitbit, Garmin, and 100+ other devices.
                  </p>
                  <Button
                    onClick={() => setActiveTab('devices')}
                    className="bg-gradient-to-r from-teal-500 to-cyan-600"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    Connect Device
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Devices Tab */}
          <TabsContent value="devices" className="space-y-6">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader>
                <CardTitle>Connect Your Wearable</CardTitle>
                <CardDescription className="text-gray-400">
                  Select your device to connect. We support 100+ wearables through our unified integration.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {connectionStatus?.is_connected ? (
                  <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-6 text-center">
                    <CheckCircle2 className="w-12 h-12 mx-auto text-green-400 mb-3" />
                    <h3 className="text-lg font-semibold text-green-400">Device Connected</h3>
                    <p className="text-gray-400 mt-1">
                      Your {connectionStatus.provider} is connected and syncing data.
                    </p>
                    <Button
                      variant="outline"
                      onClick={disconnectDevice}
                      className="mt-4 border-red-500/50 text-red-400 hover:bg-red-500/20"
                    >
                      <Unlink className="w-4 h-4 mr-2" />
                      Disconnect Device
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button
                      onClick={connectDevice}
                      disabled={connecting}
                      size="lg"
                      className="w-full mb-6 bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 h-14 text-lg"
                      data-testid="connect-wearable-btn"
                    >
                      {connecting ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          Opening Connection...
                        </>
                      ) : (
                        <>
                          <Link2 className="w-5 h-5 mr-2" />
                          Connect Any Wearable Device
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>

                    <p className="text-sm text-gray-400 text-center mb-6">
                      A new window will open where you can securely connect your device.
                    </p>

                    <div className="border-t border-white/10 pt-6">
                      <h4 className="text-sm font-medium text-gray-400 mb-4">Supported Devices</h4>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                        {supportedDevices.map((device) => {
                          const Icon = getDeviceIcon(device.id);
                          const colorClass = DEVICE_COLORS[device.id] || 'from-gray-600 to-gray-800';
                          
                          return (
                            <div
                              key={device.id}
                              className="flex flex-col items-center p-3 rounded-lg bg-white/5 border border-white/10 hover:border-white/20 transition-colors"
                            >
                              <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${colorClass} flex items-center justify-center mb-2`}>
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <span className="text-xs text-center text-gray-300">{device.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Tab */}
          <TabsContent value="activity" className="space-y-6">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-blue-400" />
                  Activity Trends
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Your daily activity over the past week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {activityData.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={activityData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="date" 
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 12 }}
                          tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short' })}
                        />
                        <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#F3F4F6' }}
                        />
                        <Area 
                          type="monotone" 
                          dataKey="steps" 
                          stroke="#3B82F6" 
                          fill="#3B82F6" 
                          fillOpacity={0.3}
                          name="Steps"
                        />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-gray-400">
                    {connectionStatus?.is_connected 
                      ? 'No activity data available yet. Sync your device to see data.'
                      : 'Connect a wearable device to see activity data.'}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Sleep Tab */}
          <TabsContent value="sleep" className="space-y-6">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Moon className="w-5 h-5 text-purple-400" />
                  Sleep Analysis
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Your sleep patterns over the past week
                </CardDescription>
              </CardHeader>
              <CardContent>
                {sleepData.length > 0 ? (
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={sleepData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                        <XAxis 
                          dataKey="start_time" 
                          stroke="#9CA3AF"
                          tick={{ fill: '#9CA3AF', fontSize: 12 }}
                          tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { weekday: 'short' })}
                        />
                        <YAxis stroke="#9CA3AF" tick={{ fill: '#9CA3AF', fontSize: 12 }} />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1F2937', border: 'none', borderRadius: '8px' }}
                          labelStyle={{ color: '#F3F4F6' }}
                        />
                        <Bar 
                          dataKey="duration_seconds" 
                          fill="#8B5CF6" 
                          name="Sleep Duration (sec)"
                          radius={[4, 4, 0, 0]}
                        />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <div className="h-80 flex items-center justify-center text-gray-400">
                    {connectionStatus?.is_connected 
                      ? 'No sleep data available yet. Sync your device to see data.'
                      : 'Connect a wearable device to see sleep data.'}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default TerraWearables;
